import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import { dashboardService } from "services/dashboard.service";
import { DashboardPerformance, DashboardStats } from "types/dashboard.type";

const toDateStr = (d: Date) => d.toISOString().split("T")[0];

const defaultDateFrom = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return toDateStr(d);
};
const defaultDateTo = () => toDateStr(new Date());

export class DashboardStore {
  stats: DashboardStats | null = null;
  performance: DashboardPerformance | null = null;
  loadingStats = false;
  loadingPerformance = false;
  dateFrom: string = defaultDateFrom();
  dateTo: string = defaultDateTo();
  selectedSubCompanyId: number | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Fetch global counts
  fetchStats = async () => {
    this.loadingStats = true;
    try {
      const stats = await dashboardService.getStats(this.selectedSubCompanyId);
      runInAction(() => {
        this.stats = stats;
      });
    } catch (error) {
      toast.error("Échec du chargement des statistiques globales");
    } finally {
      runInAction(() => {
        this.loadingStats = false;
      });
    }
  };

  // Fetch performance with current date range
  fetchPerformance = async () => {
    this.loadingPerformance = true;
    try {
      const data = await dashboardService.getPerformance(
        this.dateFrom,
        this.dateTo,
        this.selectedSubCompanyId
      );
      const normalizedPerformance: DashboardPerformance = {
        totalGain: data.totalGain ?? 0,
        totalCharges: data.totalCharges ?? 0,
        totalDefective: data.totalDefective ?? 0,
        totalReturns: data.totalReturns ?? 0,
        netProfit: data.netProfit ?? 0,
        totalAllSalesQty: data.totalAllSalesQty ?? 0,
        totalDeliveredSalesQty: data.totalDeliveredSalesQty ?? 0,
        totalDeliveredRevenue: data.totalDeliveredRevenue ?? 0,
        totalMissingQty: data.totalMissingQty ?? 0,
        totalMissingLoss: data.totalMissingLoss ?? 0,
        netGain: data.netGain ?? 0,
        topProducts:
          data.topProducts?.map((p) => ({
            name: p.name,
            quantity: p.quantity,
          })) ?? [],
        period: "month",
        chartData: data.chartData ?? [],
        productChartData:
          data.productChartData?.map((p) => ({
            name: p.name,
            quantity: p.quantity,
          })) ?? [],
      };
      runInAction(() => {
        this.performance = normalizedPerformance;
      });
    } catch (error) {
      toast.error("Échec du chargement des performances du tableau de bord");
    } finally {
      runInAction(() => (this.loadingPerformance = false));
    }
  };

  setDateRange = (dateFrom: string, dateTo: string) => {
    this.dateFrom = dateFrom;
    this.dateTo = dateTo;
    this.fetchPerformance();
  };

  setSubCompany = (subCompanyId: number | null) => {
    this.selectedSubCompanyId = subCompanyId;
    this.fetchStats();
    this.fetchPerformance();
  };
}
const dashboardStore = new DashboardStore();
export default dashboardStore;
